import { z } from "zod";

type SchemaKey<T extends z.ZodRawShape> = Extract<keyof T, string>;
type OrderableValue<T extends z.ZodRawShape> = SchemaKey<T> | `-${SchemaKey<T>}`;

/**
 * Helper function to create a Zod schema that accepts object keys with optional `-` prefix
 * Useful for sorting/ordering parameters where `-key` indicates descending order
 *
 * @example
 * const UserSchema = z.object({ id: z.number(), name: z.string() });
 * const OrderSchema = orderable(UserSchema);
 * // Accepts: "id", "name", "-id", "-name"
 */
export function orderable<T extends z.ZodRawShape>(schema: z.ZodObject<T>) {
  const keys = Object.keys(schema.shape) as Array<SchemaKey<T>>;

  return z.custom<OrderableValue<T>>(
    (value): value is OrderableValue<T> => {
      if (typeof value !== "string") {
        return false;
      }

      if (keys.includes(value as SchemaKey<T>)) {
        return true;
      }

      if (!value.startsWith("-")) {
        return false;
      }

      return keys.includes(value.slice(1) as SchemaKey<T>);
    },
    {
      message: "Invalid ordering field",
    },
  );
}
